import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import axios from '../utils/axios';
import { Lock, Mail, Eye, EyeOff, Phone, AlertCircle, KeyRound } from 'lucide-react';

const UnifiedLogin = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [formData, setFormData] = useState({
    identifier: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  
  // Custom interactive state for revamp
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);

  // Forgot Password Wizard States
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotStep, setForgotStep] = useState(1); // 1: Verify identifier, 2: Reset password
  const [forgotIdentifier, setForgotIdentifier] = useState('');
  const [forgotNewPassword, setForgotNewPassword] = useState('');
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState('');
  const [forgotRecoveryKey, setForgotRecoveryKey] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotShowPassword, setForgotShowPassword] = useState(false);
  const [forgotShowConfirmPassword, setForgotShowConfirmPassword] = useState(false);
  const [verifiedName, setVerifiedName] = useState('');

  // Check for session expired toast notification on load
  useEffect(() => {
    if (localStorage.getItem('session_expired_message') === 'true') {
      localStorage.removeItem('session_expired_message');
      toast.error('Session expired. Please log in again.', {
        duration: 5000,
        icon: '⚠️',
        id: 'session-expired-toast'
      });
    }
  }, []);

  // Handle navigation after user state is updated
  useEffect(() => {
    if (user && pendingNavigation) {
      // User state has been updated, now navigate
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  }, [user, pendingNavigation, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.identifier || !formData.password) {
      setError('Please enter email/phone and password');
      setShake(true);
      setTimeout(() => setShake(false), 600);
      return;
    }

    setLoading(true);

    const result = await login(formData.identifier, formData.password);

    if (result.success) {
      toast.success('Login successful!');

      // Determine navigation path based on user type
      let navigationPath = '/';
      if (result.user?.isFirstLogin) {
        navigationPath = '/change-password';
      } else if (result.user?.userType === 'admin') {
        navigationPath = '/admin/dashboard';
      }

      // Set pending navigation and wait for user state to update
      setPendingNavigation(navigationPath);
    } else {
      // Show clear error message
      setError('Invalid email or password. Please try again.');
      setLoading(false);
      // Trigger card shake animation
      setShake(true);
      setTimeout(() => setShake(false), 600);
    }
  };

  return (
    <div className="min-h-screen premium-mid-bg flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative overflow-hidden">

      {/* Decorative luxury ambient glow elements */}
      <div className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] bg-indigo-600/10 rounded-full filter blur-[120px] pointer-events-none"></div>
      <div className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] bg-purple-600/10 rounded-full filter blur-[120px] pointer-events-none"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-amber-500/5 rounded-full filter blur-[150px] pointer-events-none"></div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-tr from-[#C5A57D] to-[#E5C59D] rounded-full shadow-[0_0_30px_rgba(197,165,125,0.25)] mb-4 border border-[#C5A57D]/30 transition-transform duration-300 hover:scale-105">
            <Lock className="w-9 h-9 text-slate-900" />
          </div>
          <h1 className="text-5xl font-extrabold bg-gradient-to-r from-white via-slate-200 to-[#C5A57D] bg-clip-text text-transparent mb-2 tracking-tight drop-shadow-sm">
            IdealTrendz
          </h1>
          <p className="text-slate-400 text-lg font-medium tracking-wide">
            Interior Design Management System
          </p>
        </div>
      </div>

      <div className="mt-4 sm:mx-auto sm:w-full sm:max-w-md relative z-10 px-4 sm:px-0">
        <div className={`glass-card-premium py-10 px-8 sm:px-12 rounded-3xl ${shake ? 'animate-shake' : ''}`}>
          <div className="mb-8 text-center">
            <h2 className="text-3xl font-extrabold text-white mb-2 tracking-tight">
              Welcome Back
            </h2>
            <p className="text-sm text-slate-400">
              Sign in to Admin or Employee Portal
            </p>
          </div>

          {/* Form Error Alert Block with blinking glow */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 text-red-200 rounded-xl text-sm flex items-center gap-3 animate-error-blink shadow-[0_0_15px_rgba(239,68,68,0.1)]">
              <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
              <span className="font-semibold">{error}</span>
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Identifier Field (Email or Phone) */}
            <div className="group">
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-widest mb-2 transition-colors duration-200 group-focus-within:text-[#C5A57D]">
                Email ID or Phone Number
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  {/^[0-9]+$/.test(formData.identifier) ? (
                    <Phone className={`h-5 w-5 transition-colors duration-200 ${error ? 'text-red-400' : 'text-slate-500 group-focus-within:text-[#C5A57D]'}`} />
                  ) : (
                    <Mail className={`h-5 w-5 transition-colors duration-200 ${error ? 'text-red-400' : 'text-slate-500 group-focus-within:text-[#C5A57D]'}`} />
                  )}
                </div>
                <input
                  type="text"
                  required
                  value={formData.identifier}
                  onChange={(e) => {
                    setError('');
                    setFormData({ ...formData, identifier: e.target.value });
                  }}
                  className={`w-full pl-11 pr-4 py-3.5 rounded-xl placeholder-slate-500/50 transition duration-200 premium-input focus:ring-0 ${
                    error ? 'premium-input-error' : ''
                  }`}
                  placeholder="Enter email or 10-digit phone number"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="group">
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-widest mb-2 transition-colors duration-200 group-focus-within:text-[#C5A57D]">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <Lock className={`h-5 w-5 transition-colors duration-200 ${error ? 'text-red-400' : 'text-slate-500 group-focus-within:text-[#C5A57D]'}`} />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={(e) => {
                    setError('');
                    setFormData({ ...formData, password: e.target.value });
                  }}
                  className={`w-full pl-11 pr-10 py-3.5 rounded-xl placeholder-slate-500/50 transition duration-200 premium-input focus:ring-0 ${
                    error ? 'premium-input-error' : ''
                  }`}
                  placeholder="Enter your password"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-500 hover:text-slate-300 transition-colors duration-200"
                  disabled={loading}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Forgot Password Link */}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowForgotModal(true);
                  setForgotStep(1);
                  setForgotIdentifier('');
                  setForgotNewPassword('');
                  setForgotConfirmPassword('');
                  setForgotRecoveryKey('');
                  setForgotError('');
                }}
                className="text-xs font-semibold text-[#C5A57D] hover:text-[#E5C59D] transition-colors focus:outline-none uppercase tracking-wider"
              >
                Forgot Password?
              </button>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#C5A57D] to-[#E5C59D] text-slate-900 py-3.5 px-4 rounded-xl font-bold hover:shadow-[0_0_30px_rgba(197,165,125,0.35)] active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg focus:outline-none"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5 text-slate-900" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>Signing in...</span>
                </div>
              ) : (
                'Sign in'
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Direct Forgot Password Modal */}
      {showForgotModal && (
        <div className="fixed inset-0 bg-black/85 flex items-center justify-center p-4 z-50 animate-fade-in backdrop-blur-sm">
          <div className="glass-card-premium max-w-md w-full p-8 rounded-3xl border border-[#C5A57D]/20 shadow-2xl relative bg-[#0A5F38] bg-opacity-95">
            {/* Close Button */}
            <button
              onClick={() => setShowForgotModal(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors text-xl font-bold focus:outline-none"
            >
              ✕
            </button>

            {forgotStep === 1 ? (
              // Step 1: Verify Identifier
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-white mb-2">Forgot Password?</h3>
                  <p className="text-sm text-slate-400">
                    Enter your registered phone number or email to verify your account.
                  </p>
                </div>

                {forgotError && (
                  <div className="p-3.5 bg-red-500/10 border border-red-500/30 text-red-200 rounded-xl text-xs flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                    <span className="font-semibold">{forgotError}</span>
                  </div>
                )}

                <div className="group">
                  <label className="block text-xs font-bold text-slate-300 uppercase tracking-widest mb-2 group-focus-within:text-[#C5A57D]">
                    Email ID or Phone Number
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      {/^[0-9]+$/.test(forgotIdentifier) ? (
                        <Phone className="h-4 w-4 text-slate-500 group-focus-within:text-[#C5A57D]" />
                      ) : (
                        <Mail className="h-4 w-4 text-slate-500 group-focus-within:text-[#C5A57D]" />
                      )}
                    </div>
                    <input
                      type="text"
                      required
                      value={forgotIdentifier}
                      onChange={(e) => {
                        setForgotError('');
                        setForgotIdentifier(e.target.value);
                      }}
                      className="w-full pl-10 pr-4 py-3.5 rounded-xl placeholder-slate-500/50 transition duration-200 premium-input focus:ring-0 text-sm bg-black/40 border-slate-700 text-white"
                      placeholder="Enter registered email or 10-digit phone"
                      disabled={forgotLoading}
                    />
                  </div>
                </div>

                <button
                  type="button"
                  onClick={async () => {
                    if (!forgotIdentifier.trim()) {
                      setForgotError('Please enter email or phone number');
                      return;
                    }
                    setForgotLoading(true);
                    setForgotError('');
                    try {
                      const response = await axios.post('/auth/verify-identifier', {
                        identifier: forgotIdentifier.trim()
                      });
                      if (response.data?.success) {
                        setVerifiedName(response.data.name || '');
                        setForgotStep(2);
                      } else {
                        setForgotError(response.data?.error || 'Verification failed');
                      }
                    } catch (err) {
                      setForgotError(err.response?.data?.error || 'Verification failed. Account not found.');
                    } finally {
                      setForgotLoading(false);
                    }
                  }}
                  disabled={forgotLoading}
                  className="w-full bg-gradient-to-r from-[#C5A57D] to-[#E5C59D] text-slate-900 py-3.5 px-4 rounded-xl font-bold hover:shadow-[0_0_20px_rgba(197,165,125,0.25)] transition duration-200 disabled:opacity-50 text-sm"
                >
                  {forgotLoading ? 'Verifying Account...' : 'Verify Account'}
                </button>
              </div>
            ) : (
              // Step 2: Reset Password
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-white mb-1">Set New Password</h3>
                  <p className="text-sm text-green-400 font-medium">
                    ✓ Account verified for {verifiedName}
                  </p>
                </div>

                {forgotError && (
                  <div className="p-3.5 bg-red-500/10 border border-red-500/30 text-red-200 rounded-xl text-xs flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                    <span className="font-semibold">{forgotError}</span>
                  </div>
                )}

                {/* Recovery Key */}
                <div className="group">
                  <label className="block text-xs font-bold text-slate-300 uppercase tracking-widest mb-2 group-focus-within:text-[#C5A57D]">
                    Recovery Key
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <KeyRound className="h-4 w-4 text-slate-500 group-focus-within:text-[#C5A57D]" />
                    </div>
                    <input
                      type="password"
                      required
                      value={forgotRecoveryKey}
                      onChange={(e) => {
                        setForgotError('');
                        setForgotRecoveryKey(e.target.value);
                      }}
                      className="w-full pl-10 pr-4 py-3.5 rounded-xl placeholder-slate-500/50 transition duration-200 premium-input focus:ring-0 text-sm bg-black/40 border-slate-700 text-white"
                      placeholder="Enter the admin recovery key"
                      autoComplete="off"
                      disabled={forgotLoading}
                    />
                  </div>
                </div>

                {/* New Password */}
                <div className="group">
                  <label className="block text-xs font-bold text-slate-300 uppercase tracking-widest mb-2 group-focus-within:text-[#C5A57D]">
                    New Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-4 w-4 text-slate-500 group-focus-within:text-[#C5A57D]" />
                    </div>
                    <input
                      type={forgotShowPassword ? 'text' : 'password'}
                      required
                      value={forgotNewPassword}
                      onChange={(e) => {
                        setForgotError('');
                        setForgotNewPassword(e.target.value);
                      }}
                      className="w-full pl-10 pr-10 py-3.5 rounded-xl placeholder-slate-500/50 transition duration-200 premium-input focus:ring-0 text-sm bg-black/40 border-slate-700 text-white"
                      placeholder="Enter new password (min 6 chars)"
                      disabled={forgotLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setForgotShowPassword(!forgotShowPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-500 hover:text-slate-300 transition-colors"
                    >
                      {forgotShowPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div className="group">
                  <label className="block text-xs font-bold text-slate-300 uppercase tracking-widest mb-2 group-focus-within:text-[#C5A57D]">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-4 w-4 text-slate-500 group-focus-within:text-[#C5A57D]" />
                    </div>
                    <input
                      type={forgotShowConfirmPassword ? 'text' : 'password'}
                      required
                      value={forgotConfirmPassword}
                      onChange={(e) => {
                        setForgotError('');
                        setForgotConfirmPassword(e.target.value);
                      }}
                      className="w-full pl-10 pr-10 py-3.5 rounded-xl placeholder-slate-500/50 transition duration-200 premium-input focus:ring-0 text-sm bg-black/40 border-slate-700 text-white"
                      placeholder="Confirm new password"
                      disabled={forgotLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setForgotShowConfirmPassword(!forgotShowConfirmPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-500 hover:text-slate-300 transition-colors"
                    >
                      {forgotShowConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={async () => {
                    if (!forgotRecoveryKey.trim()) {
                      setForgotError('Please enter the recovery key');
                      return;
                    }
                    if (!forgotNewPassword.trim() || !forgotConfirmPassword.trim()) {
                      setForgotError('Please fill out all password fields');
                      return;
                    }
                    if (forgotNewPassword.trim().length < 6) {
                      setForgotError('Password must be at least 6 characters');
                      return;
                    }
                    if (forgotNewPassword.trim() !== forgotConfirmPassword.trim()) {
                      setForgotError('Passwords do not match');
                      return;
                    }

                    setForgotLoading(true);
                    setForgotError('');
                    try {
                      const response = await axios.post('/auth/reset-password-direct', {
                        identifier: forgotIdentifier.trim(),
                        newPassword: forgotNewPassword.trim(),
                        recoveryKey: forgotRecoveryKey.trim()
                      });
                      if (response.data?.success) {
                        toast.success('Password reset successfully! Log in to continue.');
                        setShowForgotModal(false);
                      } else {
                        setForgotError(response.data?.error || 'Failed to reset password');
                      }
                    } catch (err) {
                      setForgotError(err.response?.data?.error || 'Failed to reset password. Please try again.');
                    } finally {
                      setForgotLoading(false);
                    }
                  }}
                  disabled={forgotLoading}
                  className="w-full bg-gradient-to-r from-[#C5A57D] to-[#E5C59D] text-slate-900 py-3.5 px-4 rounded-xl font-bold hover:shadow-[0_0_20px_rgba(197,165,125,0.25)] transition duration-200 disabled:opacity-50 text-sm"
                >
                  {forgotLoading ? 'Resetting Password...' : 'Reset Password'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="mt-12 text-center text-xs text-slate-500 tracking-wider relative z-10">
        <p>© 2026 IdealTrendz. All rights reserved.</p>
      </div>
    </div>
  );
};

export default UnifiedLogin;
